/**
 * Pure Regeln des Moduls „SiGe-Handbuch".
 *
 * Hier steht alles, was ohne DOM, ohne Supabase und ohne jsdom entscheidbar
 * ist — das Projekt uebersetzt Tests in der Node-Umgebung (kein jsdom), also
 * ist eine Regel nur dann geprueft, wenn sie hier liegt und nicht in einer
 * Komponente. Stehende Entscheidung im Repo: duenner Container, Logik in
 * Hooks und puren Funktionen (Kommentar in PlanungModul.tsx).
 *
 * WOHER DIE BAUSTEINE KOMMEN — und warum von dort:
 *   * Normalisierung und Gewerkezuordnung aus `koordinierung/gewerkeKatalog.ts`.
 *     Entscheidung Nr. 9: KEIN Duplikat der Normalisierung. Waeren es zwei
 *     Implementierungen, waere dasselbe Gewerk im Handbuch ein anderes als in
 *     der Koordinierung — und niemand saehe, welches der beiden recht hat.
 *   * Datumspruefung aus `koordinierung/ueberschneidung.ts` (`alsTag`). Dort
 *     wird ISO LEXIKALISCH verglichen und niemals ueber `new Date()`, weil
 *     `toISOString()` in negativen Zeitzonen um einen Tag verschiebt.
 *   * Vorlagen, PSA- und Nachweisbausteine aus `gewerkVorlagen.ts`.
 */

import { parseDateLocal } from '../../services/dateService';
import {
    KATALOG_VERSION as GEWERKE_KATALOG_VERSION,
    UNBEKANNT,
    gewerkAusText,
    gewerkBezeichnung,
    normalisiereText,
} from '../koordinierung/gewerkeKatalog';
import type { GewerkTreffer } from '../koordinierung/gewerkeKatalog';
import { alsTag, ampel, berechneUeberschneidung, tageEinschliesslich } from '../koordinierung/ueberschneidung';
// Nur Schluesselbildung und Paarschluessel — die Datei rechnet KEINE
// Koordinierung nach, sie liest deren Ergebnis. Der Import geht bewusst gegen
// `koordinierungLogic` und nicht gegen eine eigene Abschrift: zwei
// Schluesselbildungen waeren zwei Wahrheiten darueber, was dasselbe Gewerk ist.
import { gewerkSchluessel, paarKey } from '../koordinierung/koordinierungLogic';
import type { KoordinierungPaar } from '../koordinierung/koordinierungLogic';
import type { Ampel } from '../koordinierung/types';
import { aufloesePikto } from '../sige-plan/types';
// Nur die Bruecke Nummer ↔ Wortlaut — die Nummern selbst kommen vom Steckbrief
// der Planungsphase, das Handbuch rechnet die Pruefung nicht nach.
import { istAnhangIINummer, zeilenFuerNummer, type AnhangIINummer } from '../../data/anhang2Bruecke';
import { anhangIIAusVorankuendigung, sortiereAnhangII } from './anhangII';
import {
    VORLAGEN_VERSION,
    nachweiseFuerVorlage,
    psaFuerVorlage,
    vorlageFuer,
} from './gewerkVorlagen';
import type {
    Baustelleneinrichtung,
    Baustellenordnung,
    GewerkAusPlan,
    Handbuch,
    HandbuchGewerk,
    HandbuchLage,
    HandbuchNachweis,
    HistorieEintrag,
    KapitelId,
    NeuerHandbuchNachweis,
    NeuesHandbuchGewerk,
    PlanSchritt,
    PsaEintrag,
} from './types';

// ═══════════════════════════════════════════════════════════════════════════
// KATALOGFASSUNG
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Fassung, mit der dieses Handbuch gearbeitet hat.
 *
 * BEIDE Kataloge stehen drin, weil beide das Ergebnis beeinflussen: die
 * Vorlagen bestimmen, was auf der Gewerkeseite vorbelegt ist, der Gewerke-
 * katalog bestimmt, welche Vorlage ueberhaupt gezogen wird. Eine einzelne
 * Zahl liesse spaeter offen, welcher der beiden gemeint war.
 *
 * `handbuecher.katalog_version` ist TEXT und hat bewusst keinen Vorgabewert —
 * ein Default traege irgendwann eine Fassung ein, die nie benutzt wurde.
 */
export const aktuelleKatalogVersion = (): string =>
    `vorlagen-${VORLAGEN_VERSION}/gewerke-${GEWERKE_KATALOG_VERSION}`;

// ═══════════════════════════════════════════════════════════════════════════
// KAPITEL
// ═══════════════════════════════════════════════════════════════════════════

export interface KapitelDefinition {
    id: KapitelId;
    titel: string;
    /**
     * Blockiert das Kapitel die Freigabe, solange es unvollstaendig ist?
     *
     * Nicht jedes fachlich wichtige Kapitel steht hier auf `true`. Massgeblich
     * ist, ob DIESES Modul die Vollstaendigkeit ueberhaupt beurteilen kann —
     * eine Freigabe an Daten zu haengen, die man nicht sieht, waere eine
     * Behauptung und keine Pruefung. Siehe `gefaehrdungsmatrix`.
     */
    pflicht: boolean;
}

/** Die Kapitel in Druckreihenfolge. */
export const KAPITEL: KapitelDefinition[] = [
    { id: 'deckblatt', titel: 'Deckblatt', pflicht: true },
    { id: 'organigramm', titel: 'Organigramm und Beteiligte', pflicht: true },
    { id: 'baustelleneinrichtung', titel: 'Baustelleneinrichtung', pflicht: false },
    { id: 'baustellenordnung', titel: 'Baustellenordnung', pflicht: true },
    // Die Bewertung der Gewerkepaare liegt in `koordinierung_paare` und damit
    // ausserhalb dieses Moduls. Das Kapitel wird gedruckt und in der Uebersicht
    // bewertet, blockiert die Freigabe aber nicht: eine Sperre auf Daten, die
    // hier gar nicht geladen werden, koennte man weder erklaeren noch aufheben.
    { id: 'gefaehrdungsmatrix', titel: 'Gefährdungsmatrix', pflicht: false },
    { id: 'gesamtzeitplan', titel: 'Gesamtzeitplan', pflicht: false },
    { id: 'gewerke', titel: 'Gewerkeseiten', pflicht: true },
    { id: 'dokumentenverzeichnis', titel: 'Dokumentenverzeichnis', pflicht: false },
    { id: 'historie', titel: 'Änderungshistorie', pflicht: false },
];

export const kapitelTitel = (id: KapitelId): string =>
    KAPITEL.find(k => k.id === id)?.titel ?? id;

// ═══════════════════════════════════════════════════════════════════════════
// VORBELEGUNGEN
// ═══════════════════════════════════════════════════════════════════════════

/** Portiert aus `BaustellenordnungPage.tsx:7-13` der Quell-App. */
export const DEFAULT_REGELN: string[] = [
    'Auf der gesamten Baustelle besteht Helm- und Sicherheitsschuhpflicht.',
    'Alkohol und andere berauschende Mittel sind auf der Baustelle verboten.',
    'Verkehrs- und Rettungswege sind freizuhalten.',
    'Jedes Unternehmen räumt seinen Arbeitsbereich täglich auf und entsorgt seinen Abfall selbst.',
    'Gemeinsam genutzte Einrichtungen dürfen nur nach Freigabe und unverändert benutzt werden.',
    'Unfälle, Beinaheunfälle und gefährliche Zustände sind der Bauleitung sofort zu melden.',
    'Arbeiten mit besonderer Gefährdung sind vor Beginn mit dem SiGeKo abzustimmen.',
];

/** Portiert aus `BaustellenordnungPage.tsx:15-21`, ergaenzt um den Wortlaut
 *  der Einweisung (`services/sigeEinweisung.ts`) — beide Texte muessen
 *  dasselbe sagen, sonst lernt der Eingewiesene etwas anderes, als am
 *  Bauzaun haengt. */
export const DEFAULT_NOTFALLSCHRITTE: string[] = [
    'Unfallstelle sichern, sich selbst nicht gefährden.',
    'Verletzte aus dem Gefahrenbereich bringen, wenn es ohne Eigengefährdung möglich ist.',
    'Notruf 112 absetzen: Wo? Was? Wie viele? Welche Verletzungen? Warten auf Rückfragen.',
    'Erste Hilfe leisten, Ersthelfer der Baustelle hinzuziehen.',
    'Einweiser an die Rettungszufahrt stellen.',
    'Bauleitung und SiGeKo verständigen, Unfall im Verbandbuch eintragen.',
];

/**
 * Eine LEERE Baustellenordnung — ohne die Vorbelegungen.
 *
 * ═══ WARUM HIER KEINE REGELN STEHEN ════════════════════════════════════════
 * `DEFAULT_REGELN` und `DEFAULT_NOTFALLSCHRITTE` sind ein VORSCHLAG und keine
 * Tatsache. Standen sie hier drin, schrieb `ensureHandbuch` sie beim Anlegen
 * in die Datenbank — und von da an galt das Pflichtkapitel „Baustellenordnung"
 * als vollstaendig (`kapitelBefunde`), die Freigabe war frei, die sieben
 * Saetze hingen als „Regeln auf dieser Baustelle" am Bauzaun und standen vorne
 * in der Einweisung. Ein Handbuch anlegen und sofort freigeben genuegte, damit
 * niemand je eine Regel bestaetigt hatte.
 *
 * Dasselbe Muster wie `psaVorschlag` und `anhangIIVorschlagOffen`: das Haus
 * schlaegt vor, uebernommen wird durch einen Menschen. `Baustellenordnung.tsx`
 * bietet die Liste dafuer als Knopf „Vorschlag uebernehmen" an und zeigt eine
 * leere Liste als leer.
 *
 * BESTANDSDATEN BLEIBEN UNANGETASTET: diese Funktion ist nur der Anfangswert
 * fuer NEUE Saetze und der Rueckfall, wenn `handbuecher.ordnung` gar kein
 * Objekt ist. Ein Handbuch, das die Vorbelegung bereits in der Spalte stehen
 * hat, liest sie unveraendert weiter und bleibt damit vollstaendig.
 */
export const leereOrdnung = (): Baustellenordnung => ({
    arbeitszeiten: '',
    zugang: '',
    regeln: [],
    notfallschritte: [],
    sammelplatz: '',
});

export const leereEinrichtung = (): Baustelleneinrichtung => ({
    versorgung: '',
    entsorgung: '',
    lagerplatz: '',
    verkehrsfuehrung: '',
    bemerkung: '',
});

// ═══════════════════════════════════════════════════════════════════════════
// GEWERKE AUS DEM SIGE-PLAN
// ═══════════════════════════════════════════════════════════════════════════

const kleinstes = (a: string | null, b: string | null): string | null =>
    a === null ? b : b === null ? a : (a < b ? a : b);

const groesstes = (a: string | null, b: string | null): string | null =>
    a === null ? b : b === null ? a : (a > b ? a : b);

/**
 * Gruppiert die Arbeitsschritte einer Planfassung zu Gewerkeseiten.
 *
 * ═══ WARUM DER SCHLUESSEL NUR NORMALISIERT UND NICHT ZUGEORDNET IST ════════
 * `gewerk_norm` entsteht aus `normalisiereText` und NICHT aus `gewerkAusText`.
 * Die Zuordnung des Katalogs fuehrt „Elektro" und „Elektro NU 2" auf dieselbe
 * id — fuer die Ueberschneidungsanalyse ist das richtig, fuer das Handbuch
 * waere es falsch: die beiden sind zwei Firmen mit zwei Gefaehrdungs-
 * beurteilungen und zwei Nachweismappen. Wuerden sie hier automatisch
 * verschmelzen, verschwaende eine Firma aus dem Handbuch, ohne dass es jemand
 * bemerkt. Die Katalog-id wird trotzdem ermittelt — aber nur, um die richtige
 * VORLAGE zu ziehen.
 *
 * Zusammengelegt wird ausschliesslich per Klick (`fuehreGewerkeZusammen`).
 */
export const gewerkeAusPlan = (schritte: PlanSchritt[]): GewerkAusPlan[] => {
    interface Sammler {
        norm: string;
        schreibweisen: Map<string, number>;
        von: string | null;
        bis: string | null;
        schrittIds: string[];
    }
    const nach = new Map<string, Sammler>();

    (schritte || []).forEach(s => {
        const roh = (s?.gewerk || '').trim();
        if (!roh) return;                       // Ohne Gewerk keine Gewerkeseite.
        const norm = normalisiereText(roh);
        if (!norm) return;                      // Nur Satzzeichen — kein Gewerk.

        let eintrag = nach.get(norm);
        if (!eintrag) {
            eintrag = { norm, schreibweisen: new Map(), von: null, bis: null, schrittIds: [] };
            nach.set(norm, eintrag);
        }
        eintrag.schreibweisen.set(roh, (eintrag.schreibweisen.get(roh) || 0) + 1);
        eintrag.von = kleinstes(eintrag.von, alsTag(s.zeitraum_von));
        eintrag.bis = groesstes(eintrag.bis, alsTag(s.zeitraum_bis));
        if (s.id) eintrag.schrittIds.push(s.id);
    });

    const liste: GewerkAusPlan[] = [...nach.values()].map(e => {
        // Haeufigste Schreibweise gewinnt; bei Gleichstand die zuerst
        // aufgetretene, damit die Reihenfolge im Plan entscheidet und nicht die
        // Laune der Map-Iteration.
        const schreibweisen = [...e.schreibweisen.entries()]
            .sort((a, b) => b[1] - a[1])
            .map(x => x[0]);
        const label = schreibweisen[0] || e.norm;
        const treffer = gewerkAusText(label);
        return {
            gewerk_norm: e.norm,
            gewerk_label: label,
            katalogId: treffer === UNBEKANNT ? null : treffer,
            zeitraum_von: e.von,
            zeitraum_bis: e.bis,
            schrittIds: e.schrittIds,
            schreibweisen,
        };
    });

    // Nach Baubeginn, Gewerke ohne Datum ans Ende — aber SICHTBAR und nicht
    // weggelassen. Ein stillschweigend fehlendes Gewerk ist der gefaehrlichere
    // Fehler: das Handbuch saehe vollstaendig aus.
    return liste.sort((a, b) => {
        if (a.zeitraum_von && b.zeitraum_von && a.zeitraum_von !== b.zeitraum_von) {
            return a.zeitraum_von < b.zeitraum_von ? -1 : 1;
        }
        if (a.zeitraum_von && !b.zeitraum_von) return -1;
        if (!a.zeitraum_von && b.zeitraum_von) return 1;
        return a.gewerk_label.localeCompare(b.gewerk_label, 'de');
    });
};

/** Anzeigename der zugeordneten Katalogfassung, oder ein ehrlicher Satz. */
export const katalogHinweis = (katalogId: string | null): string =>
    katalogId
        ? gewerkBezeichnung(katalogId as GewerkTreffer)
        : 'Keinem Katalog-Gewerk zugeordnet — Vorlage bitte von Hand wählen.';

/**
 * Baut den Entwurf einer Gewerkeseite aus Plan und Vorlage.
 *
 * `zeitraum_von`/`_bis` bleiben ausdruecklich NULL, obwohl der Plan sie
 * hergibt: NULL heisst laut Migration „aus den Arbeitsschritten rechnen".
 * Schriebe man die Werte fest, bliebe eine spaetere Planaenderung im Handbuch
 * unsichtbar — und der Zeitplan zeigte etwas anderes als der Plan.
 */
export const entwurfAusGewerk = (
    gewerk: GewerkAusPlan,
    sortierung: number,
): NeuesHandbuchGewerk => {
    const vorlage = vorlageFuer(gewerk.katalogId);
    return {
        gewerk_norm: gewerk.gewerk_norm,
        gewerk_label: gewerk.gewerk_label,
        contact_id: null,
        din_nummer: vorlage?.dinNummer || null,
        firma: null,
        verantwortlich: null,
        telefon: null,
        anzahl_beschaeftigte: null,
        zeitraum_von: null,
        zeitraum_bis: null,
        anhang_ii: sortiereAnhangII(vorlage?.anhangII ?? []),
        psa: psaFuerVorlage(vorlage),
        unterweisungen: [],
        notfall: { ersthelfer: '', besonderheiten: '' },
        witterung: { eintraege: [] },
        rab32: { eintraege: [] },
        ordnung: { regeln: [] },
        sortierung,
    };
};

/**
 * Die geforderten Nachweise einer Gewerkeseite als Entwuerfe.
 *
 * Status ist `fehlt` — und das ist KEIN Fehlerzustand: der Nachweis wird
 * gefordert, bevor er da ist. Die Zeile mit `fehlt` und abgelaufener Frist ist
 * der beweiskraeftige Teil dieser Tabelle.
 */
export const nachweisEntwuerfe = (katalogId: string | null): NeuerHandbuchNachweis[] =>
    nachweiseFuerVorlage(vorlageFuer(katalogId)).map((bezeichnung, i) => ({
        nr: String(i + 1),
        bezeichnung,
        status: 'fehlt',
        frist: null,
        bemerkung: '',
        dokument_id: null,
        sortierung: i,
    }));

// ═══════════════════════════════════════════════════════════════════════════
// ABGLEICH PLAN ↔ HANDBUCH
// ═══════════════════════════════════════════════════════════════════════════

export interface GewerkAbgleich {
    /** Im Plan, aber noch ohne Gewerkeseite. */
    neu: GewerkAusPlan[];
    /** Im Plan und im Handbuch. */
    bekannt: GewerkAusPlan[];
    /**
     * Gewerkeseite ohne Entsprechung im aktuellen Plan.
     *
     * Wird MARKIERT und nicht geloescht — dieselbe Regel wie bei den veralteten
     * Paaren der Koordinierung. Wer ein Gewerk aus dem Plan nimmt, loescht
     * damit nicht die Nachweise, die er dafuer eingesammelt hat.
     */
    verwaist: HandbuchGewerk[];
}

export const abgleicheGewerke = (
    ausPlan: GewerkAusPlan[],
    gespeichert: HandbuchGewerk[],
): GewerkAbgleich => {
    const imHandbuch = new Set((gespeichert || []).map(g => g.gewerk_norm));
    const imPlan = new Set((ausPlan || []).map(g => g.gewerk_norm));
    return {
        neu: (ausPlan || []).filter(g => !imHandbuch.has(g.gewerk_norm)),
        bekannt: (ausPlan || []).filter(g => imHandbuch.has(g.gewerk_norm)),
        verwaist: (gespeichert || []).filter(g => !imPlan.has(g.gewerk_norm)),
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// GEWERKE ZUSAMMENFUEHREN
// ═══════════════════════════════════════════════════════════════════════════

const psaSchluessel = (p: PsaEintrag): string =>
    `${(p.bezeichnung || '').trim().toLowerCase()}|${(p.norm || '').trim().toLowerCase()}`;

const ersterWert = <T>(werte: (T | null | undefined)[]): T | null => {
    for (const w of werte) {
        if (w === null || w === undefined) continue;
        if (typeof w === 'string' && !w.trim()) continue;
        return w;
    }
    return null;
};

/** Ein Nachweis, der beim Zusammenfuehren an eine andere Gewerkeseite geht. */
export interface NachweisUmhaengung {
    nachweisId: string;
    vonGewerkId: string;
    nachGewerkId: string;
}

export interface Zusammenfuehrung {
    /** Die Zielseite mit allem, was aus den Quellen dazugekommen ist. */
    ziel: HandbuchGewerk;
    /** Die Seiten, die danach entfallen. */
    aufgeloest: HandbuchGewerk[];
    /** Jeder Nachweis der aufgeloesten Seiten, einzeln benannt. */
    umhaengungen: NachweisUmhaengung[];
    /** Alle Schreibweisen, die jetzt auf die Zielseite zeigen — gehoert in den
     *  Aenderungsvermerk, sonst weiss spaeter niemand mehr, was zusammengelegt
     *  wurde. */
    schreibweisen: string[];
    /**
     * Was die Zusammenfuehrung VERLOREN hat.
     *
     * Wird unabhaengig vom Zusammenbauen nachgerechnet und muss leer sein. Der
     * Wert steht hier, damit die Oberflaeche ihn zeigen kann statt ihn zu
     * unterstellen — eine stillschweigend verlorene Zuordnung ist genau der
     * Fehler, den bei dieser Funktion niemand bemerken wuerde.
     */
    verloren: { nachweisIds: string[]; anhangII: string[]; psa: string[] };
}

/**
 * Legt mehrere Gewerkeseiten auf eine zusammen.
 *
 * NICHTS wird ueberschrieben: leere Felder der Zielseite werden aus den
 * Quellen gefuellt, gefuellte bleiben stehen. Listen werden vereinigt, nicht
 * ersetzt. Der Zeitraum wird zur Huelle beider Zeitraeume — sonst faellt eine
 * Bauphase aus dem Zeitplan.
 *
 * Die Funktion SCHREIBT NICHT. Sie liefert, was geschrieben werden muss; der
 * Dienst fuehrt es aus. So laesst sich das Ergebnis pruefen, bevor es
 * irgendwo landet.
 */
export const fuehreGewerkeZusammen = (
    ziel: HandbuchGewerk,
    quellen: HandbuchGewerk[],
    nachweise: HandbuchNachweis[],
): Zusammenfuehrung => {
    const echteQuellen = (quellen || []).filter(q => q && q.id !== ziel.id);
    const alle = [ziel, ...echteQuellen];

    // ── Listen vereinigen ────────────────────────────────────────────────
    const anhangII = sortiereAnhangII(alle.flatMap(g => g.anhang_ii || []));

    const psaGesehen = new Set<string>();
    const psa: PsaEintrag[] = [];
    alle.forEach(g => (g.psa || []).forEach(p => {
        const k = psaSchluessel(p);
        if (psaGesehen.has(k)) return;
        psaGesehen.add(k);
        psa.push(p);
    }));

    const unterGesehen = new Set<string>();
    const unterweisungen = alle.flatMap(g => g.unterweisungen || []).filter(u => {
        const k = (u.thema || '').trim().toLowerCase();
        if (!k || unterGesehen.has(k)) return false;
        unterGesehen.add(k);
        return true;
    });

    const regelGesehen = new Set<string>();
    const regeln = alle.flatMap(g => g.ordnung?.regeln || []).filter(r => {
        const k = (r || '').trim().toLowerCase();
        if (!k || regelGesehen.has(k)) return false;
        regelGesehen.add(k);
        return true;
    });

    const witterung = alle.flatMap(g => g.witterung?.eintraege || []);
    const rab32 = alle.flatMap(g => g.rab32?.eintraege || []);

    // ── Einzelfelder: Zielwert schlaegt Quellwert ────────────────────────
    const zusammengefuehrt: HandbuchGewerk = {
        ...ziel,
        contact_id: ersterWert(alle.map(g => g.contact_id)),
        din_nummer: ersterWert(alle.map(g => g.din_nummer)),
        firma: ersterWert(alle.map(g => g.firma)),
        verantwortlich: ersterWert(alle.map(g => g.verantwortlich)),
        telefon: ersterWert(alle.map(g => g.telefon)),
        anzahl_beschaeftigte: ersterWert(alle.map(g => g.anzahl_beschaeftigte)),
        zeitraum_von: alle.reduce<string | null>((a, g) => kleinstes(a, g.zeitraum_von), null),
        zeitraum_bis: alle.reduce<string | null>((a, g) => groesstes(a, g.zeitraum_bis), null),
        anhang_ii: anhangII,
        psa,
        unterweisungen,
        notfall: {
            ersthelfer: ersterWert(alle.map(g => g.notfall?.ersthelfer)) || '',
            besonderheiten: ersterWert(alle.map(g => g.notfall?.besonderheiten)) || '',
        },
        witterung: { eintraege: witterung },
        rab32: { eintraege: rab32 },
        ordnung: { regeln },
    };

    // ── Nachweise umhaengen ──────────────────────────────────────────────
    const quellIds = new Set(echteQuellen.map(q => q.id));
    const umhaengungen: NachweisUmhaengung[] = (nachweise || [])
        .filter(n => quellIds.has(n.handbuch_gewerk_id))
        .map(n => ({ nachweisId: n.id, vonGewerkId: n.handbuch_gewerk_id, nachGewerkId: ziel.id }));

    // ── Gegenrechnung ────────────────────────────────────────────────────
    // Bewusst unabhaengig vom Zusammenbauen oben nachgezaehlt. Eine Pruefung,
    // die dieselben Variablen benutzt wie die Rechnung, prueft nichts.
    const erwarteteNachweise = (nachweise || [])
        .filter(n => quellIds.has(n.handbuch_gewerk_id))
        .map(n => n.id);
    const umgehaengt = new Set(umhaengungen.map(u => u.nachweisId));
    const anhangDanach = new Set(zusammengefuehrt.anhang_ii.map(a => a.trim()));
    const psaDanach = new Set(zusammengefuehrt.psa.map(psaSchluessel));

    return {
        ziel: zusammengefuehrt,
        aufgeloest: echteQuellen,
        umhaengungen,
        schreibweisen: [...new Set(alle.map(g => g.gewerk_label).filter(Boolean))],
        verloren: {
            nachweisIds: erwarteteNachweise.filter(id => !umgehaengt.has(id)),
            anhangII: [...new Set(alle.flatMap(g => g.anhang_ii || []).map(a => a.trim()))]
                .filter(a => a && !anhangDanach.has(a)),
            psa: [...new Set(alle.flatMap(g => g.psa || []).map(psaSchluessel))]
                .filter(k => !psaDanach.has(k)),
        },
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// NACHWEISE UND FRISTEN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Fristlage eines Nachweises.
 *
 * `erledigt` steht VOR allen Datumsfaellen: ein vorhandener Nachweis mit
 * abgelaufener Frist ist kein Mangel, sondern erledigt. Die Frist war der
 * Termin, bis zu dem er beizubringen war — nicht sein Verfallsdatum.
 */
export type FristLage = 'erledigt' | 'ueberfaellig' | 'bald' | 'ok' | 'ohne_frist';

/**
 * Ab wie vielen Tagen vor Fristablauf gewarnt wird.
 *
 * 14 Tage, weil ein fehlender Nachweis in der Regel bei einer fremden Firma
 * angefordert werden muss und die Antwort ueber ein Wochenende laeuft. Kuerzer
 * gewarnt hiesse: warnen, wenn es schon zu spaet ist.
 */
export const FRIST_VORWARNUNG_TAGE = 14;

/**
 * Verbleibende Tage bis zur Frist. Negativ heisst „vorbei".
 *
 * Rechnet ueber `tageEinschliesslich` aus der Koordinierung und damit OHNE
 * `Date`: der String-Konstruktor legt „2026-01-01" als Mitternacht UTC aus,
 * und in einer negativen Zeitzone kippt daraus beim Zurueckrechnen der 31.12.
 * Genau dieser Fehler steckt in `hazard-matrix.ts:31` der Quell-App und wird
 * hier NICHT mitportiert. `tageEinschliesslich` zaehlt beide Grenzen mit,
 * deshalb das `- 1`: heute faellige Frist ergibt 0 Resttage.
 */
export const restTage = (heuteIso: string, fristIso: string): number =>
    tageEinschliesslich(heuteIso, fristIso) - 1;

export const fristLage = (
    nachweis: Pick<HandbuchNachweis, 'status' | 'frist'>,
    heuteIso: string,
): FristLage => {
    if (nachweis.status === 'vorhanden') return 'erledigt';
    const frist = alsTag(nachweis.frist);
    const heute = alsTag(heuteIso);
    if (!frist || !heute) return 'ohne_frist';
    // Lexikalischer Vergleich fuer das „ist es schon vorbei?", Tagesrechnung
    // nur fuer den Abstand — so entscheidet keine Zeitzone ueber das Ergebnis.
    if (frist < heute) return 'ueberfaellig';
    return restTage(heute, frist) <= FRIST_VORWARNUNG_TAGE ? 'bald' : 'ok';
};

export interface NachweisUebersicht {
    gesamt: number;
    vorhanden: number;
    inPruefung: number;
    fehlt: number;
    ueberfaellig: number;
    bald: number;
    /** Anteil vorhandener Nachweise in Prozent, kaufmaennisch gerundet. */
    quote: number;
    /** Nachweise mit Status `vorhanden`, aber ohne Beleg in der Projektablage.
     *  Fachlich der unangenehmste Fall: es sieht erledigt aus und ist es nicht
     *  nachweisbar. */
    ohneBeleg: number;
}

export const nachweisUebersicht = (
    nachweise: HandbuchNachweis[],
    heuteIso: string,
): NachweisUebersicht => {
    const liste = nachweise || [];
    const zaehl = (f: (n: HandbuchNachweis) => boolean) => liste.filter(f).length;
    const vorhanden = zaehl(n => n.status === 'vorhanden');
    return {
        gesamt: liste.length,
        vorhanden,
        inPruefung: zaehl(n => n.status === 'pruefung'),
        fehlt: zaehl(n => n.status === 'fehlt'),
        ueberfaellig: zaehl(n => fristLage(n, heuteIso) === 'ueberfaellig'),
        bald: zaehl(n => fristLage(n, heuteIso) === 'bald'),
        quote: liste.length ? Math.round((vorhanden / liste.length) * 100) : 0,
        ohneBeleg: zaehl(n => n.status === 'vorhanden' && !n.dokument_id),
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// VOLLSTAENDIGKEIT JE KAPITEL
// ═══════════════════════════════════════════════════════════════════════════

export interface KapitelBefund {
    kapitel: KapitelId;
    titel: string;
    pflicht: boolean;
    vollstaendig: boolean;
    /** Was konkret fehlt — fertige Saetze, damit Bildschirm und PDF dieselbe
     *  Auskunft geben. */
    fehlend: string[];
    /**
     * Anmerkung, die das Kapitel NICHT unvollstaendig macht.
     *
     * Getrennt von `fehlend`, weil sonst ein Kapitel entstuende, das nie
     * vollstaendig werden kann: die Gefaehrdungsmatrix wird in einem anderen
     * Modul bewertet: „von hier nicht pruefbar" ist ein Hinweis und kein Mangel.
     */
    hinweis?: string;
}

const leer = (wert: string | null | undefined): boolean => !(wert || '').trim();

/**
 * Beurteilt jedes Kapitel.
 *
 * `planGewerke` ist optional und wird NUR fuer den Gesamtzeitplan gebraucht:
 * eine Gewerkeseite mit `zeitraum_von = NULL` ist kein Mangel, solange der
 * Plan einen Zeitraum hergibt (NULL heisst laut Migration „aus dem Plan
 * rechnen"). Ohne die Plandaten kann diese eine Frage nicht beantwortet
 * werden — deshalb steht sie als Parameter und wird nicht geraten.
 */
export const kapitelBefunde = (
    lage: HandbuchLage,
    heuteIso: string,
    planGewerke: GewerkAusPlan[] = [],
): KapitelBefund[] => {
    const hb = lage.handbuch;
    const gewerke = lage.gewerke || [];
    const nachweise = lage.nachweise || [];
    const uebersicht = nachweisUebersicht(nachweise, heuteIso);
    const planNach = new Map(planGewerke.map(g => [g.gewerk_norm, g]));

    const mangel = (bedingung: boolean, satz: string): string[] => (bedingung ? [satz] : []);

    const befund = (kapitel: KapitelId, fehlend: string[], hinweis?: string): KapitelBefund => {
        const def = KAPITEL.find(k => k.id === kapitel);
        return {
            kapitel,
            titel: def?.titel ?? kapitel,
            pflicht: def?.pflicht ?? false,
            vollstaendig: fehlend.length === 0,
            fehlend,
            ...(hinweis ? { hinweis } : {}),
        };
    };

    if (!hb) {
        // Ohne Kopfsatz ist jedes Kapitel offen — und zwar mit demselben Grund.
        return KAPITEL.map(k => befund(k.id, ['Es wurde noch kein Handbuch angelegt.']));
    }

    return [
        befund('deckblatt', [
            ...mangel(!hb.org_modell, 'Das Vergabemodell ist noch nicht bestimmt.'),
            ...mangel(leer(hb.stand), 'Der Stand des Handbuchs fehlt.'),
            ...mangel(!hb.gueltig_von, 'Der Beginn der Gültigkeit fehlt.'),
        ]),
        befund('organigramm', [
            ...mangel(!hb.org_modell, 'Ohne Vergabemodell lässt sich kein Organigramm zeichnen.'),
        ]),
        befund('baustelleneinrichtung', [
            ...mangel(leer(hb.einrichtung?.versorgung), 'Die Versorgung (Strom, Wasser, Sanitär) ist nicht beschrieben.'),
            ...mangel(leer(hb.einrichtung?.entsorgung), 'Die Entsorgung ist nicht beschrieben.'),
            ...mangel(leer(hb.einrichtung?.lagerplatz), 'Die Lagerflächen sind nicht festgelegt.'),
        ]),
        befund('baustellenordnung', [
            ...mangel(leer(hb.ordnung?.arbeitszeiten), 'Die Arbeitszeiten fehlen.'),
            ...mangel(leer(hb.ordnung?.zugang), 'Die Zugangsregelung fehlt.'),
            ...mangel(leer(hb.ordnung?.sammelplatz), 'Der Sammelplatz fehlt.'),
            ...mangel(!(hb.ordnung?.regeln || []).length, 'Es ist keine einzige Regel festgelegt.'),
            ...mangel(!(hb.ordnung?.notfallschritte || []).length, 'Die Notfallschritte fehlen.'),
        ]),
        befund(
            'gefaehrdungsmatrix',
            [],
            gewerke.length >= 2
                ? 'Die Bewertung der Gewerkepaare wird im Modul „Gewerke-Überschneidung" geführt und ist von hier aus nicht prüfbar.'
                : 'Unter zwei Gewerken gibt es keine Paarung zu bewerten.',
        ),
        befund('gesamtzeitplan', gewerke
            .filter(g => !(g.zeitraum_von && g.zeitraum_bis)
                && !(planNach.get(g.gewerk_norm)?.zeitraum_von && planNach.get(g.gewerk_norm)?.zeitraum_bis))
            .map(g => `${g.gewerk_label}: kein Zeitraum — weder im Handbuch noch im Plan.`)),
        befund('gewerke', [
            ...mangel(!gewerke.length, 'Es gibt noch keine Gewerkeseite.'),
            ...gewerke.filter(g => leer(g.firma)).map(g => `${g.gewerk_label}: die ausführende Firma fehlt.`),
            ...gewerke.filter(g => leer(g.verantwortlich)).map(g => `${g.gewerk_label}: der Verantwortliche fehlt.`),
            ...gewerke.filter(g => !(g.psa || []).length).map(g => `${g.gewerk_label}: es ist keine PSA festgelegt.`),
        ]),
        befund('dokumentenverzeichnis', [
            ...mangel(uebersicht.ueberfaellig > 0, `${uebersicht.ueberfaellig} Nachweis(e) sind überfällig.`),
            ...mangel(uebersicht.ohneBeleg > 0, `${uebersicht.ohneBeleg} Nachweis(e) gelten als vorhanden, sind aber keinem Dokument der Projektablage zugeordnet.`),
        ]),
        befund('historie', [
            ...mangel(!(hb.historie || []).length, 'Es ist noch keine Fassung vermerkt.'),
        ]),
    ];
};

/** Anteil vollstaendiger Kapitel in Prozent. */
export const vollstaendigkeitsQuote = (befunde: KapitelBefund[]): number =>
    befunde.length
        ? Math.round((befunde.filter(b => b.vollstaendig).length / befunde.length) * 100)
        : 0;

// ═══════════════════════════════════════════════════════════════════════════
// SEITENZAEHLUNG
// ═══════════════════════════════════════════════════════════════════════════

export interface SeitenEintrag {
    nr: number;
    kapitel: KapitelId;
    titel: string;
    /** Nur bei Gewerkeseiten belegt. */
    gewerkId?: string;
}

/**
 * Der Seitenspiegel des gedruckten Handbuchs.
 *
 * Wird gebraucht, BEVOR gedruckt wird: Kopf und Fuss jeder A3-Seite tragen
 * „Seite x von y", und y kennt man erst, wenn alle Kapitel gezaehlt sind.
 * Deshalb hier als pure Funktion und nicht im Generator — sonst zaehlten
 * Vorschau und PDF getrennt und kaemen irgendwann auf verschiedene Zahlen.
 *
 * Jedes Kapitel belegt genau eine Seite; die Gewerkeseiten eine je Gewerk.
 * Gibt es keine Gewerke, entfaellt das Kapitel — eine leere Seite mit der
 * Ueberschrift „Gewerkeseiten" behauptet, es gaebe keine Gewerke, und das ist
 * etwas anderes als „noch keine erfasst".
 */
export const seitenPlan = (lage: HandbuchLage): SeitenEintrag[] => {
    const seiten: SeitenEintrag[] = [];
    const gewerke = [...(lage.gewerke || [])].sort((a, b) => a.sortierung - b.sortierung);

    KAPITEL.forEach(k => {
        if (k.id !== 'gewerke') {
            seiten.push({ nr: seiten.length + 1, kapitel: k.id, titel: k.titel });
            return;
        }
        gewerke.forEach(g => {
            seiten.push({
                nr: seiten.length + 1,
                kapitel: 'gewerke',
                titel: `Gewerk: ${g.gewerk_label}`,
                gewerkId: g.id,
            });
        });
    });

    return seiten;
};

export const zaehleSeiten = (lage: HandbuchLage): number => seitenPlan(lage).length;

// ═══════════════════════════════════════════════════════════════════════════
// FREIGABE UND SPERRE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ist das Handbuch eingefroren?
 *
 * Entscheidung Nr. 8: `status='freigegeben'` sperrt die Kapitel. Eine spaetere
 * Aenderung erzeugt eine NEUE Fassung in `historie` und ueberschreibt die
 * freigegebene nicht. Vorbild ist die abgeschlossene Einweisung — dort wird
 * das Blatt eingefroren, damit ein spaeterer Planwechsel das unterschriebene
 * Dokument nicht nachtraeglich verfaelscht.
 */
export const istGesperrt = (handbuch: Handbuch | null | undefined): boolean =>
    handbuch?.status === 'freigegeben';

/** Der Satz zur Sperre, oder `null`, wenn nicht gesperrt. */
export const sperrHinweis = (handbuch: Handbuch | null | undefined): string | null => {
    if (!istGesperrt(handbuch)) return null;
    const wer = (handbuch?.freigegeben_von_name || '').trim();
    const wann = (handbuch?.freigegeben_am || '').slice(0, 10);
    const teil = [wer && `durch ${wer}`, wann && `am ${wann}`].filter(Boolean).join(' ');
    return `Das Handbuch ist freigegeben${teil ? ` (${teil})` : ''} und damit eingefroren. `
        + 'Für Änderungen eine neue Fassung eröffnen — die freigegebene Fassung bleibt erhalten.';
};

export interface FreigabePruefung {
    freigabeMoeglich: boolean;
    hinweis: string;
    /** Nur die Pflichtkapitel, die die Freigabe blockieren. */
    offeneKapitel: KapitelId[];
    /** Was in den Pflichtkapiteln konkret fehlt. */
    offenePunkte: string[];
}

/**
 * Darf freigegeben werden?
 *
 * Blockierend sind ausschliesslich die PFLICHTKAPITEL und ueberfaellige
 * Nachweise. Alles andere ist ein Hinweis: ein Handbuch mit unvollstaendiger
 * Baustelleneinrichtung ist unschoen, aber es ist kein Grund, die Freigabe zu
 * verweigern und damit den Aushang aufzuhalten.
 *
 * Ein bereits freigegebenes Handbuch kann nicht noch einmal freigegeben
 * werden — dafuer gibt es die neue Fassung.
 */
export const pruefeFreigabe = (
    lage: HandbuchLage,
    heuteIso: string,
    planGewerke: GewerkAusPlan[] = [],
): FreigabePruefung => {
    if (!lage.handbuch) {
        return {
            freigabeMoeglich: false,
            hinweis: 'Es wurde noch kein Handbuch angelegt.',
            offeneKapitel: [],
            offenePunkte: [],
        };
    }
    if (istGesperrt(lage.handbuch)) {
        return {
            freigabeMoeglich: false,
            hinweis: sperrHinweis(lage.handbuch) || 'Das Handbuch ist bereits freigegeben.',
            offeneKapitel: [],
            offenePunkte: [],
        };
    }

    const befunde = kapitelBefunde(lage, heuteIso, planGewerke);
    const offene = befunde.filter(b => b.pflicht && !b.vollstaendig);
    const uebersicht = nachweisUebersicht(lage.nachweise || [], heuteIso);
    const offenePunkte = offene.flatMap(b => b.fehlend);

    if (offene.length) {
        return {
            freigabeMoeglich: false,
            hinweis: `${offene.length} Pflichtkapitel ${offene.length === 1 ? 'ist' : 'sind'} noch unvollständig.`,
            offeneKapitel: offene.map(b => b.kapitel),
            offenePunkte,
        };
    }
    if (uebersicht.ueberfaellig > 0) {
        return {
            freigabeMoeglich: false,
            hinweis: `${uebersicht.ueberfaellig} Nachweis(e) sind überfällig. `
                + 'Ein Handbuch mit abgelaufenen Nachweisfristen freizugeben hieße, den Mangel zu beurkunden.',
            offeneKapitel: [],
            offenePunkte: [],
        };
    }
    return {
        freigabeMoeglich: true,
        hinweis: 'Alle Pflichtkapitel sind vollständig, keine Nachweisfrist ist abgelaufen. '
            + 'Mit der Freigabe wird das Handbuch eingefroren.',
        offeneKapitel: [],
        offenePunkte: [],
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// HISTORIE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die naechste Fassungsnummer, zweistellig als „1.0", „2.0" …
 *
 * Bewusst aus dem BESTAND abgeleitet und nicht mitgezaehlt: die Historie ist
 * die einzige Wahrheit darueber, wie viele Fassungen es gab. Ein separater
 * Zaehler liefe bei jedem Wiederherstellen aus einer Sicherung auseinander.
 */
export const naechsteFassungsNummer = (historie: HistorieEintrag[]): string => {
    const zahlen = (historie || [])
        .map(h => Number.parseInt(String(h.nr || '').split('.')[0], 10))
        .filter(n => Number.isFinite(n));
    const hoechste = zahlen.length ? Math.max(...zahlen) : 0;
    return `${hoechste + 1}.0`;
};

/**
 * Haengt einen Eintrag an die Historie an — und ersetzt NIE einen bestehenden.
 *
 * Genau das ist die Zusage von Entscheidung Nr. 8: eine spaetere Aenderung
 * ueberschreibt die freigegebene Fassung nicht, sie stellt sich daneben.
 */
export const ergaenzeHistorie = (
    historie: HistorieEintrag[],
    eintrag: HistorieEintrag,
): HistorieEintrag[] => [...(historie || []), eintrag];

export const freigabeEintrag = (
    handbuch: Handbuch,
    autor: string,
    heuteIso: string,
    aenderungen: string,
): HistorieEintrag => ({
    nr: naechsteFassungsNummer(handbuch.historie || []),
    datum: (alsTag(heuteIso) || heuteIso).slice(0, 10),
    autor: (autor || '').trim(),
    aenderungen: (aenderungen || '').trim() || 'Freigabe der Fassung.',
    version_label: handbuch.version_label || '',
});

/**
 * Ein Historieneintrag MIT dem Planstand, auf dem die Freigabe beruht.
 *
 * ═══ WARUM DER VERMERK IN DER HISTORIE STEHT ═══════════════════════════════
 * `handbuecher` hat keine Spalte fuer die bewertete Planfassung — geprueft in
 * `supabase/handbuch_koordinierung.sql`; die einzige Stelle, an der dort eine
 * `sige_projekt_id` vorkommt, ist `koordinierung_paare`. Eine Spalte zu
 * erfinden hiesse, eine Migration zu behaupten, die es nicht gibt. `historie`
 * ist dagegen eine vorhandene jsonb-Spalte OHNE Formvorgabe ausser
 * `jsonb_typeof = 'array'`, und sie ist ohnehin der Ort, an dem eine Fassung
 * beschrieben wird.
 *
 * Der Vermerk gehoert JE FASSUNG und nicht ans Handbuch: die zweite Freigabe
 * beurteilt einen anderen Planstand als die erste, und beide muessen
 * nachlesbar bleiben.
 */
export interface FreigabeVermerk extends HistorieEintrag {
    /**
     * `sige_projekte.id` der Planfassung, die dieser Freigabe zugrunde lag.
     * `null`, wenn es zum Zeitpunkt der Freigabe keinen Plan gab — das ist
     * eine Aussage und kein fehlender Wert.
     */
    sige_projekt_id?: string | null;
}

/** Haengt den Planstand an einen Freigabevermerk. */
export const mitPlanfassung = (
    eintrag: HistorieEintrag,
    sigeProjektId: string | null,
): FreigabeVermerk => ({ ...eintrag, sige_projekt_id: sigeProjektId || null });

/**
 * Die Planfassung, auf der die geltende Freigabe beruht — oder `null`.
 *
 * Nur fuer ein EINGEFRORENES Handbuch: ein Entwurf liest bewusst weiter den
 * laufenden Plan, denn er soll die Verschiebung ja zeigen. Gesucht wird von
 * hinten, weil die letzte Freigabe die geltende ist; aeltere Vermerke bleiben
 * als Beleg der frueheren Fassungen stehen.
 */
export const planfassungDerFreigabe = (
    handbuch: Handbuch | null | undefined,
): string | null => {
    if (!istGesperrt(handbuch)) return null;
    const eintraege = (handbuch?.historie || []) as FreigabeVermerk[];
    for (let i = eintraege.length - 1; i >= 0; i -= 1) {
        const id = (eintraege[i]?.sige_projekt_id || '').trim();
        if (id) return id;
    }
    return null;
};

// ═══════════════════════════════════════════════════════════════════════════
// AUS DEN KAPITELDATEIEN HIERHER GEZOGEN (Verdrahtung)
//
// Die neun Kapitel entstanden in zwei Wellen und trugen ihre reinen Funktionen
// jeweils oben in der eigenen Datei — mehrfach dieselben. `wirksamerZeitraum`
// stand dreimal da, `verbinde` viermal. Das ist nicht bloss Doppelarbeit:
// solange eine Regel in einer Komponentendatei steht, ist sie UNGEPRUEFT (das
// Projekt uebersetzt Tests ohne jsdom), und drei Abschriften laufen bei der
// ersten Korrektur auseinander. Alles, was eine fachliche Aussage traegt oder
// mehr als einmal vorkam, steht deshalb ab hier.
//
// Was ABSICHTLICH in den Kapiteln geblieben ist: Darstellungsschnipsel ohne
// Aussage (Prozentformatierung, Kastengeometrie des Organigramms, Rangfolgen
// fuer die Sortierung genau einer Tabelle).
// ═══════════════════════════════════════════════════════════════════════════

// ─── Textbausteine ───────────────────────────────────────────────────────

/** Nicht-leere Teile mit Trennzeichen verbinden. Stand in vier Kapiteln. */
export const verbinde = (
    teile: (string | undefined | null)[],
    trenner = ' · ',
): string => (teile || []).map(t => (t || '').trim()).filter(Boolean).join(trenner);

/**
 * Ein Tagesdatum in deutscher Schreibweise.
 *
 * Ueber `parseDateLocal` und NICHT ueber `new Date(iso)`: der String-
 * Konstruktor legt „2026-01-01" als Mitternacht UTC aus, und in einer
 * negativen Zeitzone zeigt `toLocaleDateString` daraus den 31.12.2025. Genau
 * dieser Fehler steckt in `hazard-matrix.ts` der Quell-App und wird hier nicht
 * mitportiert.
 */
export const tagText = (iso?: string | null): string => {
    const d = parseDateLocal(iso);
    return d ? d.toLocaleDateString('de-DE') : '';
};

/** Monat und Jahr — im Gewerkekasten des Organigramms ist fuer ein
 *  Tagesdatum kein Platz. */
export const monatText = (iso?: string | null): string => {
    const d = parseDateLocal(iso);
    return d ? `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}` : '';
};

/** Zeitspanne als „von … bis …". Leer, wenn beide Enden fehlen — ein
 *  halboffener Zeitraum wird dagegen gezeigt, er ist eine echte Angabe. */
export const spanneText = (von?: string | null, bis?: string | null): string => {
    const a = tagText(von);
    const b = tagText(bis);
    if (a && b) return `${a} bis ${b}`;
    if (a) return `ab ${a}`;
    if (b) return `bis ${b}`;
    return '';
};

/** Text auf `max` Zeichen kappen, mit Auslassungszeichen. */
export const kuerze = (text: string, max: number): string => {
    const t = text || '';
    return t.length > max ? `${t.slice(0, max - 1)}…` : t;
};

/** Nicht-leere Werte ohne Dubletten, verglichen ohne Gross-/Kleinschreibung;
 *  die zuerst gefundene Schreibweise bleibt stehen. */
export const eindeutig = (werte: (string | undefined | null)[]): string[] => {
    const gesehen = new Set<string>();
    const raus: string[] = [];
    for (const w of werte || []) {
        const t = (w || '').trim();
        if (!t || gesehen.has(t.toLowerCase())) continue;
        gesehen.add(t.toLowerCase());
        raus.push(t);
    }
    return raus;
};

// ─── Zeitraum einer Gewerkeseite ─────────────────────────────────────────

/**
 * Der Zeitraum, mit dem eine Gewerkeseite rechnet.
 *
 * `handbuch_gewerke.zeitraum_von = NULL` heisst laut Migration „aus dem Plan
 * rechnen" — deshalb der Rueckgriff auf die Gewerke der Planfassung. Ein am
 * Handbuch gesetzter Zeitraum uebersteuert den Plan (vertraglich abweichende
 * Bauzeit) und wird NICHT stillschweigend ueberschrieben.
 *
 * Stand dreimal wortgleich in den Kapiteln (Matrix, Zeitplan, Gewerkeseite).
 */
export const wirksamerZeitraum = (
    gewerk: Pick<HandbuchGewerk, 'gewerk_norm' | 'zeitraum_von' | 'zeitraum_bis'>,
    ausPlan: Map<string, GewerkAusPlan>,
): { von: string | null; bis: string | null } => {
    const plan = ausPlan.get(gewerk.gewerk_norm);
    return {
        von: gewerk.zeitraum_von ?? plan?.zeitraum_von ?? null,
        bis: gewerk.zeitraum_bis ?? plan?.zeitraum_bis ?? null,
    };
};

/**
 * Der Zeitraum EINER FASSUNG — fuer ein freigegebenes Handbuch ohne Rueckgriff
 * auf den laufenden Plan.
 *
 * Der Rueckgriff in `wirksamerZeitraum` ist im Entwurf richtig und in einer
 * freigegebenen Fassung falsch: `zeitraum_von = NULL` heisst dort nicht mehr
 * „aus dem Plan rechnen", sondern „bei der Freigabe gab der Plan nichts her".
 * Wuerde der Plan spaeter einen Zeitraum bekommen, erschiene er im selben
 * Druck derselben freigegebenen Fassung — genau die rueckwirkende Aenderung,
 * gegen die die Freigabe gedacht ist.
 *
 * Die gefuellten Enden schreibt `freigebenHandbuch` beim Einfrieren in die
 * Zeile (siehe `einzufrierendeZeitraeume`); diese Funktion sorgt dafuer, dass
 * auch die LEER GEBLIEBENEN Enden leer bleiben.
 */
export const wirksamerZeitraumDerFassung = (
    gewerk: Pick<HandbuchGewerk, 'gewerk_norm' | 'zeitraum_von' | 'zeitraum_bis'>,
    ausPlan: Map<string, GewerkAusPlan>,
    handbuch: Handbuch | null | undefined,
): { von: string | null; bis: string | null } => (
    istGesperrt(handbuch)
        ? { von: gewerk.zeitraum_von ?? null, bis: gewerk.zeitraum_bis ?? null }
        : wirksamerZeitraum(gewerk, ausPlan)
);

/** Was beim Einfrieren in EINE Gewerkezeile geschrieben wird. Ein Feld fehlt,
 *  wenn es nicht angefasst werden darf. */
export interface ZeitraumEinfrierung {
    id: string;
    zeitraum_von?: string;
    zeitraum_bis?: string;
}

/**
 * Die Zeitraeume, die eine Freigabe festhalten muss.
 *
 * ═══ WARUM DAS UEBERHAUPT NOETIG IST ═══════════════════════════════════════
 * Eine Gewerkeseite mit `zeitraum_von = NULL` rechnet ihren Zeitraum bei JEDEM
 * Druck aus dem laufenden SiGe-Plan. Fuer einen Entwurf ist das gewollt. Eine
 * freigegebene Fassung dagegen wuerde sich dadurch rueckwirkend aendern:
 * Fassung 1.0, freigegeben am 01.09. mit Rohbau 01.03.–30.06., zeigte nach
 * einer Planverschiebung am 15.09. im GLEICHEN Druck 30.09. — ohne
 * Historieneintrag und ohne dass jemand etwas am Handbuch getan haette.
 * Dieselbe Ueberlegung friert die abgeschlossene Einweisung ihr Blatt ein
 * (sige-plan/types.ts).
 *
 * ═══ ZWEI REGELN, DIE HIER TRAGEN ══════════════════════════════════════════
 * (1) NUR LEERE ENDEN. Ein am Handbuch gesetzter Zeitraum ist eine Entscheidung
 *     (vertraglich abweichende Bauzeit) und wird niemals ueberschrieben —
 *     deshalb je Ende einzeln entschieden und nicht je Zeile.
 * (2) NUR, WO DER PLAN ETWAS HERGIBT. Ohne Planwert bleibt NULL stehen; ein
 *     erfundenes Datum waere schlimmer als eine ehrliche Luecke.
 *
 * Das Ergebnis ist damit auch beim zweiten Lauf stabil: was einmal
 * festgehalten ist, taucht hier nicht wieder auf.
 */
export const einzufrierendeZeitraeume = (
    gewerke: Pick<HandbuchGewerk, 'id' | 'gewerk_norm' | 'zeitraum_von' | 'zeitraum_bis'>[],
    ausPlan: Map<string, GewerkAusPlan>,
): ZeitraumEinfrierung[] => {
    const raus: ZeitraumEinfrierung[] = [];
    (gewerke || []).forEach(g => {
        const plan = ausPlan.get(g.gewerk_norm);
        const eintrag: ZeitraumEinfrierung = { id: g.id };
        if (!g.zeitraum_von && plan?.zeitraum_von) eintrag.zeitraum_von = plan.zeitraum_von;
        if (!g.zeitraum_bis && plan?.zeitraum_bis) eintrag.zeitraum_bis = plan.zeitraum_bis;
        if (eintrag.zeitraum_von || eintrag.zeitraum_bis) raus.push(eintrag);
    });
    return raus;
};

// ─── DIE BRUECKE ZWISCHEN DEN ZWEI SCHLUESSELRAEUMEN ─────────────────────

/**
 * Der Koordinierungsschluessel einer Gewerkeseite.
 *
 * ═══ DIE GEFAEHRLICHSTE STELLE DES MODULS ══════════════════════════════════
 * Es gibt ZWEI Schluesselraeume fuer Gewerke, und beide sind an ihrem Ort
 * richtig:
 *
 *   * `handbuch_gewerke.gewerk_norm` entsteht aus `normalisiereText` und
 *     trennt „Elektro" von „Elektro NU 2" — richtig fuer das Handbuch: das
 *     sind zwei Firmen mit zwei Gefaehrdungsbeurteilungen und zwei
 *     Nachweismappen.
 *   * `koordinierung_paare.gewerk_a_norm` entsteht aus `gewerkSchluessel`
 *     (Katalog-id, sonst `unbekannt:<slug>`) und fuehrt beide zusammen —
 *     richtig fuer die Ueberschneidungsanalyse: beide arbeiten gleichzeitig
 *     im selben Bauteil.
 *
 * Ohne diese Umrechnung faende die Gefaehrdungsmatrix zu KEINEM Paar eine
 * Bewertung und saehe leer aus. Eine leere Matrix liest sich wie eine
 * Entwarnung — deshalb steht die Bruecke hier und nicht in einer Komponente:
 * hier ist sie geprueft (siehe „Durchstich" in handbuchLogic.test.ts).
 *
 * ═══ WARUM UEBER `gewerk_norm` UND NICHT UEBER `gewerk_label` ══════════════
 * Beide ergeben dasselbe, solange niemand umbenennt: `gewerk_norm` IST
 * `normalisiereText(gewerk_label)`, und `gewerkSchluessel` normalisiert
 * seinerseits als Erstes. `gewerk_label` ist aber von Hand aenderbar
 * (`updateHandbuchGewerk` sperrt nur id, project_id, handbuch_id und
 * katalog_version), `gewerk_norm` nicht. Wer die Anzeige von „Elektro" auf
 * „Fa. Meier" umschreibt, verloere ueber das Label jede gespeicherte
 * Bewertung — ueber den maschinellen Schluessel nicht. Das Label bleibt die
 * Rueckfallebene fuer den Fall, dass `gewerk_norm` leer ist.
 */
export const koordinierungsSchluessel = (
    gewerk: Pick<HandbuchGewerk, 'gewerk_norm' | 'gewerk_label'>,
): string => gewerkSchluessel((gewerk.gewerk_norm || '').trim() || gewerk.gewerk_label);

/**
 * Die Arbeitsschritte einer Gewerkeseite.
 *
 * Verglichen wird ueber `normalisiereText` — genau die Funktion, mit der
 * `gewerkeAusPlan` den Schluessel `gewerk_norm` gebildet hat. Ein zweiter,
 * eigener Vergleich lieferte bei der ersten abweichenden Schreibweise eine
 * andere Menge als die Gewerkeliste, und dann stuenden auf der Seite
 * Taetigkeiten, die woanders zu einem anderen Gewerk gehoeren.
 */
export const schritteDesGewerks = <T extends { gewerk?: string | null }>(
    gewerk: Pick<HandbuchGewerk, 'gewerk_norm'>,
    schritte: T[],
): T[] => (schritte || []).filter(s => normalisiereText((s?.gewerk || '').trim()) === gewerk.gewerk_norm);

// ─── Gefaehrdungsmatrix ──────────────────────────────────────────────────

/** Was in einer Matrixzelle steht — bevor irgendetwas gezeichnet wird. */
export type ZellenLage =
    | 'diagonale'            // ein Gewerk gefaehrdet sich nicht selbst
    | 'gleicher_schluessel'  // zwei Seiten, die die Koordinierung als EIN Gewerk fuehrt
    | 'bewertet'
    | 'unbewertet'           // gemeinsame Zeit, aber niemand hat sie beurteilt
    | 'ohne_ueberschneidung';

export interface Zelle {
    lage: ZellenLage;
    ampel: Ampel;
    von: string | null;
    bis: string | null;
    dauerTage: number;
    /** Alle Bewertungen dieses Paares, bestaetigte zuerst. */
    bewertungen: KoordinierungPaar[];
}

/** Bestaetigte Bewertungen zuerst, ueberholte zuletzt — was ein Mensch
 *  abgezeichnet hat, gehoert oben in die Zelle. */
export const sortiereBewertungen = (liste: KoordinierungPaar[]): KoordinierungPaar[] =>
    [...(liste || [])].sort((a, b) => {
        const rang = (z: KoordinierungPaar) => (z.veraltet_seit ? 2 : z.bestaetigt_am ? 0 : 1);
        return rang(a) - rang(b);
    });

/**
 * Baut das Zellenraster der Gefaehrdungsmatrix.
 *
 * Die Farbe richtet sich nach der HEUTE gerechneten Ueberschneidung und nicht
 * nach `dauer_tage` der gespeicherten Zeile: die Zeile haelt fest, was zum
 * Zeitpunkt der Bewertung galt, die Seite zeigt den aktuellen Stand. Nur wenn
 * sich nichts rechnen laesst (Gewerk ohne Datum), wird auf den gespeicherten
 * Wert zurueckgegriffen — sonst verloere eine bewertete Zelle ihre Farbe,
 * bloss weil jemand ein Datum aus dem Plan genommen hat.
 */
export const baueZellen = (
    gewerke: HandbuchGewerk[],
    ausPlan: Map<string, GewerkAusPlan>,
    paare: KoordinierungPaar[],
): Zelle[][] => {
    const liste = gewerke || [];
    const zeitraeume = liste.map(g => wirksamerZeitraum(g, ausPlan));
    const schluessel = liste.map(koordinierungsSchluessel);

    const nachPaar = new Map<string, KoordinierungPaar[]>();
    (paare || []).forEach(z => {
        const key = paarKey(z.gewerk_a_norm || '', z.gewerk_b_norm || '');
        const vorhanden = nachPaar.get(key);
        if (vorhanden) vorhanden.push(z);
        else nachPaar.set(key, [z]);
    });

    return liste.map((_zeile, i) => liste.map((_spalte, j) => {
        if (i === j) {
            return { lage: 'diagonale' as const, ampel: 'grau' as Ampel, von: null, bis: null, dauerTage: 0, bewertungen: [] };
        }
        if (schluessel[i] === schluessel[j]) {
            return { lage: 'gleicher_schluessel' as const, ampel: 'grau' as Ampel, von: null, bis: null, dauerTage: 0, bewertungen: [] };
        }

        const fenster = berechneUeberschneidung(zeitraeume[i], zeitraeume[j]);
        const bewertungen = sortiereBewertungen(nachPaar.get(paarKey(schluessel[i], schluessel[j])) || []);

        // Ersatzdauer aus der gespeicherten Zeile, wenn sich nichts rechnen
        // laesst. `?? 0` ist hier richtig: `ampel(0)` ergibt grau, und grau
        // heisst „keine gemeinsame Zeit" — die Zelle sagt daneben trotzdem,
        // dass eine Bewertung vorliegt.
        const dauerTage = fenster ? fenster.dauerTage : (bewertungen[0]?.dauer_tage ?? 0);

        if (!fenster && bewertungen.length === 0) {
            return { lage: 'ohne_ueberschneidung' as const, ampel: 'grau' as Ampel, von: null, bis: null, dauerTage: 0, bewertungen: [] };
        }

        return {
            lage: (bewertungen.length > 0 ? 'bewertet' : 'unbewertet') as ZellenLage,
            ampel: ampel(dauerTage),
            von: fenster ? fenster.von : bewertungen[0]?.ueberschneidung_von ?? null,
            bis: fenster ? fenster.bis : bewertungen[0]?.ueberschneidung_bis ?? null,
            dauerTage,
            bewertungen,
        };
    }));
};

/** Wie viele Paare eine zeitliche Ueberschneidung haben, aber keine Bewertung.
 *  Diese Zahl steht im Kopf der Seite — sie ist die eigentliche Aussage ueber
 *  den Bearbeitungsstand. Gezaehlt wird nur die obere Dreiecksmatrix, sonst
 *  zaehlte jedes Paar doppelt. */
export const zaehleUnbewertet = (zellen: Zelle[][]): number => {
    let zahl = 0;
    (zellen || []).forEach((zeile, i) => zeile.forEach((z, j) => {
        if (i < j && z.lage === 'unbewertet') zahl += 1;
    }));
    return zahl;
};

export interface OffeneUeberschneidung {
    a: string;
    b: string;
    von: string;
    bis: string;
    dauerTage: number;
}

/** Die zeitlichen Ueberschneidungen der Gewerkeseiten, laengste zuerst — fuer
 *  den Hinweisfall, wenn noch keine einzige Bewertung vorliegt. */
export const offeneUeberschneidungen = (
    gewerke: HandbuchGewerk[],
    ausPlan: Map<string, GewerkAusPlan>,
): OffeneUeberschneidung[] => {
    const liste = gewerke || [];
    const zeitraeume = liste.map(g => wirksamerZeitraum(g, ausPlan));
    const raus: OffeneUeberschneidung[] = [];
    for (let i = 0; i < liste.length; i++) {
        for (let j = i + 1; j < liste.length; j++) {
            const fenster = berechneUeberschneidung(zeitraeume[i], zeitraeume[j]);
            if (!fenster) continue;
            raus.push({
                a: liste[i].gewerk_label,
                b: liste[j].gewerk_label,
                von: fenster.von,
                bis: fenster.bis,
                dauerTage: fenster.dauerTage,
            });
        }
    }
    return raus.sort((x, y) => y.dauerTage - x.dauerTage);
};

/** Wie viele Bewertungen zu einem Gewerkepaar vorliegen, getrennt nach
 *  bestaetigt und offen. Ueberholtes zaehlt nicht als aktuelle Bewertung —
 *  es bleibt stehen, aber es deckt nichts mehr ab. */
export const zaehleBewertungen = (
    paare: KoordinierungPaar[],
): Map<string, { bestaetigt: number; offen: number }> => {
    const nach = new Map<string, { bestaetigt: number; offen: number }>();
    (paare || []).forEach(z => {
        if (z.veraltet_seit) return;
        const key = paarKey(z.gewerk_a_norm || '', z.gewerk_b_norm || '');
        const eintrag = nach.get(key) || { bestaetigt: 0, offen: 0 };
        if (z.bestaetigt_am) eintrag.bestaetigt += 1;
        else eintrag.offen += 1;
        nach.set(key, eintrag);
    });
    return nach;
};

// ─── Gemeinsam genutzte Einrichtungen ────────────────────────────────────

/** Wortlaut aus `HAUPTABSCHNITTE` (`sige-plan/types.ts`). Kleingeschrieben
 *  verglichen, weil der Hauptabschnitt Freitext ist und in Altbestaenden
 *  abweichend geschrieben sein kann. */
export const GEMEINSAM_PRAEFIX = 'gemeinsam genutzte einrichtung';

/** Eine gemeinsam genutzte Einrichtung, aus den Arbeitsschritten abgeleitet. */
export interface Einrichtung {
    /** Der Teil hinter dem Doppelpunkt, z. B. „VERKEHRSWEGE". */
    bezeichnung: string;
    /** Die Arbeitsschritte, die darunter stehen. */
    taetigkeiten: string[];
    /** Alle Gewerke, die dort vorkommen — die Mitnutzer. */
    mitnutzer: string[];
    /** `zustaendig_kontrolle` der beteiligten Schritte. */
    verantwortlich: string[];
    /** Kein Gewerk eingetragen: die Einrichtung ist geplant, aber niemand ist
     *  ihr zugeordnet. Das ist eine Feststellung und kein Anzeigefehler. */
    ohneMitnutzer: boolean;
}

/** Ein Arbeitsschritt, so weit die Einrichtungsrechnung ihn braucht. Eigene,
 *  schmale Form aus demselben Grund wie `PlanSchritt`: die Regel bleibt ohne
 *  den ganzen Planzweig pruefbar. */
export interface EinrichtungsSchritt {
    gewerk?: string | null;
    bezeichnung?: string | null;
    hauptabschnitt?: string | null;
    zustaendig_kontrolle?: string | null;
}

/**
 * Die gemeinsam genutzten Einrichtungen des Projekts.
 *
 * Reihenfolge = Reihenfolge im Plan (die Schritte kommen sortiert herein).
 * Bewusst KEINE feste Reihenfolge nach `HAUPTABSCHNITTE`: der Plan ist das
 * Original, und das Handbuch soll ihn wiedergeben, nicht umsortieren.
 */
export const gemeinsameEinrichtungen = (schritte: EinrichtungsSchritt[]): Einrichtung[] => {
    const nach = new Map<string, Einrichtung>();
    for (const s of schritte || []) {
        const abschnitt = (s?.hauptabschnitt || '').trim();
        if (!abschnitt.toLowerCase().startsWith(GEMEINSAM_PRAEFIX)) continue;
        const doppelpunkt = abschnitt.indexOf(':');
        const bezeichnung = doppelpunkt >= 0 ? abschnitt.slice(doppelpunkt + 1).trim() : abschnitt;
        const schluessel = bezeichnung.toLowerCase();
        const vorhanden = nach.get(schluessel);
        const eintrag: Einrichtung = vorhanden ?? {
            bezeichnung,
            taetigkeiten: [],
            mitnutzer: [],
            verantwortlich: [],
            ohneMitnutzer: false,
        };
        if (s.bezeichnung?.trim()) eintrag.taetigkeiten.push(s.bezeichnung.trim());
        if (s.gewerk?.trim()) eintrag.mitnutzer.push(s.gewerk.trim());
        if (s.zustaendig_kontrolle?.trim()) eintrag.verantwortlich.push(s.zustaendig_kontrolle.trim());
        if (!vorhanden) nach.set(schluessel, eintrag);
    }
    return [...nach.values()].map(e => ({
        ...e,
        taetigkeiten: eindeutig(e.taetigkeiten),
        mitnutzer: eindeutig(e.mitnutzer),
        verantwortlich: eindeutig(e.verantwortlich),
        ohneMitnutzer: eindeutig(e.mitnutzer).length === 0,
    }));
};

/** Eine gemeinsame Einrichtung aus der Sicht EINER Gewerkeseite. */
export interface GewerkEinrichtung {
    bezeichnung: string;
    mitnutzer: string[];
    verantwortlich: string;
}

/**
 * Die gemeinsam genutzten Einrichtungen, an denen ein bestimmtes Gewerk
 * beteiligt ist.
 *
 * Mitnutzer sind die Gewerke, die im selben Hauptabschnitt Arbeitsschritte
 * haben — abgeleitet und nicht erfasst, denn doppelt gepflegt liefe es mit dem
 * Plan auseinander.
 */
export const einrichtungenDesGewerks = (
    eigene: EinrichtungsSchritt[],
    alle: EinrichtungsSchritt[],
): GewerkEinrichtung[] => {
    // Verglichen wird KLEINGESCHRIEBEN — genau wie in
    // `gemeinsameEinrichtungen`. Der Hauptabschnitt ist Freitext; in
    // Bestandsplaenen steht derselbe Abschnitt einmal „Gemeinsam genutzte
    // Einrichtung: VERKEHRSWEGE" und einmal klein geschrieben. Verglich man
    // zeichengenau, zaehlte die Gewerkeseite nur ihre eigenen Zeilen als
    // Mitnutzer — und behauptete damit, ein Gewerk nutze den Verkehrsweg
    // allein. Die projektweite Aufstellung im Kapitel „Baustelleneinrichtung"
    // fasste ihn daneben richtig zusammen; zwei Blaetter desselben Handbuchs
    // sagten Verschiedenes.
    const meine = new Map<string, string>();
    (eigene || []).forEach(s => {
        const abschnitt = (s?.hauptabschnitt || '').trim();
        const schluessel = abschnitt.toLowerCase();
        if (!schluessel.includes(GEMEINSAM_PRAEFIX) || meine.has(schluessel)) return;
        meine.set(schluessel, abschnitt);
    });

    return [...meine.entries()]
        .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
        .map(([schluessel, abschnitt]) => {
            const geteilt = (alle || [])
                .filter(s => (s?.hauptabschnitt || '').trim().toLowerCase() === schluessel);
            return {
                bezeichnung: abschnitt.replace(/^gemeinsam genutzte Einrichtung:\s*/i, ''),
                mitnutzer: eindeutig(geteilt.map(s => s.gewerk)),
                verantwortlich: eindeutig(geteilt.map(s => s.zustaendig_kontrolle)).join(', '),
            };
        });
};

// ─── Gewerkeseite: PSA und Anhang II ─────────────────────────────────────

/** Die BG-BAU-Bausteine eines Schritts — neue Mehrfachliste und Altfeld. Das
 *  Altfeld mitzulesen ist kein Zierrat: Bestandsplaene tragen nur
 *  `hinweis_code`, und deren PSA fiele sonst weg. */
export const bausteineDesSchritts = (
    schritt: { hinweis_codes?: string[] | null; hinweis_code?: string | null },
): string[] => {
    const viele = Array.isArray(schritt?.hinweis_codes) ? schritt.hinweis_codes : [];
    const einer = schritt?.hinweis_code ? [schritt.hinweis_code] : [];
    return [...viele, ...einer].filter(Boolean);
};

/**
 * Die PSA, die sich aus dem Plan und der Gewerkvorlage ergibt — abzueglich
 * dessen, was schon auf der Seite steht.
 *
 * Aus `hinweis_codes` kommen die Sicherheitszeichen, die im SiGe-Plan an den
 * Arbeitsschritten dieses Gewerks haengen; sie sind die belastbarere Quelle,
 * weil sie projektbezogen gesetzt wurden. Die Vorlage ergaenzt, was ein
 * solches Gewerk typischerweise braucht.
 *
 * NUR Gebotszeichen sind PSA. Ein Warndreieck „Absturzgefahr" gehoert zu den
 * Gefaehrdungen und nicht in die Ausruestungsliste — wer es dort einsortiert,
 * behauptet eine Schutzausruestung, die es nicht gibt.
 */
export const psaVorschlag = (
    gewerk: Pick<HandbuchGewerk, 'psa'>,
    schritte: { hinweis_codes?: string[] | null; hinweis_code?: string | null }[],
    katalogId: string | null,
): PsaEintrag[] => {
    const vorhanden = new Set((gewerk.psa || []).map(psaSchluessel));
    const raus: PsaEintrag[] = [];
    const merke = (eintrag: PsaEintrag) => {
        const key = psaSchluessel(eintrag);
        if (vorhanden.has(key)) return;
        vorhanden.add(key);
        raus.push(eintrag);
    };

    (schritte || []).forEach(s => bausteineDesSchritts(s).forEach(code => {
        const zeichen = aufloesePikto(code);
        if (!zeichen || zeichen.typ !== 'gebot') return;
        merke({ bezeichnung: zeichen.label, norm: `ISO 7010 ${zeichen.iso}`, taetigkeiten: '' });
    }));

    psaFuerVorlage(vorlageFuer(katalogId)).forEach(merke);
    return raus;
};

/** Anhang-II-Arbeiten aus der Vorankuendigung, die auf dieser Seite noch nicht
 *  angekreuzt sind. Die Vorankuendigung gilt fuers PROJEKT — welche davon
 *  dieses Gewerk ausfuehrt, weiss nur der Koordinator. */
export const anhangIIVorschlagOffen = (
    gewerk: Pick<HandbuchGewerk, 'anhang_ii'>,
    ausProjekt: string[],
): string[] => {
    const gesetzt = new Set(gewerk.anhang_ii || []);
    // Die Vorankuendigung speichert Kennungen („absturz"), das Handbuch
    // Wortlaute. Ohne Uebersetzung traf der Vergleich nie, und angezeigt
    // wurde die rohe Kennung.
    return anhangIIAusVorankuendigung(ausProjekt).filter(a => !gesetzt.has(a));
};

/** Ein Hinweis der Planungsphase an einer Gewerkeseite (Plan Stufe 3.6). */
export interface AnhangIIPlanungHinweis {
    nummer: AnhangIINummer;
    /** Die Handbuch-Wortlaute, die diese Nummer abdecken — leer, wenn der Katalog keinen fuehrt. */
    wortlaute: string[];
    /** Der fertige Satz, damit Bildschirm und Druck dasselbe sagen. */
    text: string;
}

/**
 * Anhang-II-Nummern, die laut Planungsphase zutreffen, aber auf DIESER
 * Gewerkeseite nicht angekreuzt sind.
 *
 * ═══ HINWEIS, KEIN VORSCHLAG ═══════════════════════════════════════════════
 * Anders als bei der Vorankuendigung (`anhangIIVorschlagOffen`) gibt es hier
 * nichts zu uebernehmen: die Planungsphase stellt fest, dass im PROJEKT
 * z. B. Nr. 1 vorkommt — ob das Absturz oder Verschuettung ist und welches
 * Gewerk betroffen ist, sagt sie nicht. Ein Knopf „uebernehmen" muesste raten.
 * Der Hinweis benennt die Luecke und ueberlaesst das Kreuz dem Koordinator;
 * automatisch angekreuzt wird nie.
 *
 * Die Bruecke Nummer ↔ Wortlaut haelt `data/anhang2Bruecke.ts`: eine Nummer
 * gilt als abgedeckt, sobald EINER ihrer Wortlaute angekreuzt ist („Tauch-
 * geraete / Druckluft" deckt Nr. 7 und Nr. 8 zugleich). Unbekannte oder
 * doppelte Nummern fallen weg; die Reihenfolge ist aufsteigend.
 */
export const anhangIIPlanungHinweise = (
    gewerk: Pick<HandbuchGewerk, 'anhang_ii'>,
    nummern: readonly number[] | null | undefined,
): AnhangIIPlanungHinweis[] => {
    const gesetzt = new Set((gewerk.anhang_ii || []).map(a => (a || '').trim()));
    const gueltig = [...new Set((nummern || []).filter(istAnhangIINummer))].sort((a, b) => a - b);
    const raus: AnhangIIPlanungHinweis[] = [];
    for (const nummer of gueltig) {
        const wortlaute = [...new Set(zeilenFuerNummer(nummer).flatMap(z => z.handbuch as readonly string[]))];
        if (wortlaute.some(w => gesetzt.has(w))) continue;
        raus.push({
            nummer,
            wortlaute,
            text: `laut Planungsphase Anhang II Nr. ${nummer} — für dieses Gewerk nicht angekreuzt`,
        });
    }
    return raus;
};

// ─── Dokumentenverzeichnis ───────────────────────────────────────────────

/** Der Modulschluessel, unter dem ein Dokument zum Handbuch gehoert.
 *  Wortgleich mit `DokumentModul` und dem CHECK der Tabelle. */
export const HANDBUCH_MODUL = 'handbuch';

/** Ein Dokument der Projektablage, so weit das Verzeichnis es braucht. */
export interface VerzeichnisDokument {
    id: string;
    titel?: string | null;
    kategorie?: string | null;
    module?: string[] | null;
    nur_admin?: boolean | null;
}

export interface VerzeichnisEintrag<D extends VerzeichnisDokument = VerzeichnisDokument> {
    dokument: D;
    /** Die Nachweise, die auf dieses Dokument zeigen — meist genau einer. */
    nachweise: HandbuchNachweis[];
    /** Ueber welchen Weg das Dokument ins Verzeichnis kam. */
    ueberModul: boolean;
}

export interface Verzeichnis<D extends VerzeichnisDokument = VerzeichnisDokument> {
    eintraege: VerzeichnisEintrag<D>[];
    /** Wie viele zugehoerige Dokumente ein Admin-Schloss tragen. Sie stehen
     *  NICHT in `eintraege` — nur ihre Zahl wird genannt. */
    gesperrt: number;
    /** Nachweise, deren `dokument_id` ins Leere zeigt: das Dokument wurde
     *  geloescht (`on delete set null` greift nur beim Loeschen ueber die
     *  Datenbank) oder ist fuer den Leser gesperrt. Wird benannt, nicht
     *  geschluckt. */
    belegFehlt: HandbuchNachweis[];
}

/**
 * Stellt das Dokumentenverzeichnis zusammen.
 *
 * Sortiert nach Kategorie und dann nach Titel — ein Verzeichnis wird gesucht,
 * nicht gelesen, und die Kategorie ist das, wonach jemand blaettert. Bewusst
 * NICHT nach Hochladedatum wie die Ablage selbst: dort zaehlt „was ist neu",
 * hier „wo finde ich es".
 *
 * `kategorieText` kommt von aussen, damit diese Datei die Kataloge der Ablage
 * nicht kennen muss; die Kapitelseite reicht `kategorieLabel` herein.
 */
export const baueVerzeichnis = <D extends VerzeichnisDokument>(
    dokumente: D[],
    nachweise: HandbuchNachweis[],
    kategorieText: (kategorie: string) => string,
): Verzeichnis<D> => {
    const nachDokument = new Map<string, HandbuchNachweis[]>();
    (nachweise || []).forEach(n => {
        if (!n.dokument_id) return;
        const liste = nachDokument.get(n.dokument_id);
        if (liste) liste.push(n);
        else nachDokument.set(n.dokument_id, [n]);
    });

    const eintraege: VerzeichnisEintrag<D>[] = [];
    const gefunden = new Set<string>();
    let gesperrt = 0;

    (dokumente || []).forEach(d => {
        const ueberModul = Array.isArray(d.module) && d.module.includes(HANDBUCH_MODUL);
        const ueberNachweis = nachDokument.has(d.id);
        if (!ueberModul && !ueberNachweis) return;

        gefunden.add(d.id);
        if (d.nur_admin) {
            // Nur zaehlen. Titel und Dateiname eines gesperrten Dokuments
            // gehoeren nicht auf ein Blatt, das ausgehaendigt wird.
            gesperrt += 1;
            return;
        }
        eintraege.push({ dokument: d, nachweise: nachDokument.get(d.id) || [], ueberModul });
    });

    eintraege.sort((a, b) => {
        const ka = kategorieText(a.dokument.kategorie || '');
        const kb = kategorieText(b.dokument.kategorie || '');
        if (ka !== kb) return ka < kb ? -1 : 1;
        const ta = (a.dokument.titel || '').toLowerCase();
        const tb = (b.dokument.titel || '').toLowerCase();
        return ta < tb ? -1 : ta > tb ? 1 : 0;
    });

    const belegFehlt = (nachweise || []).filter(n => n.dokument_id && !gefunden.has(n.dokument_id));

    return { eintraege, gesperrt, belegFehlt };
};

/** Die geforderten Nachweise, fuer die es noch gar kein Dokument gibt. Sie
 *  sind der eigentliche Zweck dieses Verzeichnisses: es zeigt nicht, was da
 *  ist, sondern was fehlt. */
export const offeneNachweise = (nachweise: HandbuchNachweis[]): HandbuchNachweis[] =>
    (nachweise || []).filter(n => !n.dokument_id);
